import { useEffect, useRef, useState, useCallback } from 'react';
import progressSignal from '../../utils/progressSignal';

/**
 * GlobalProgressBar — Linear / Stripe-style top progress indicator.
 *
 * - Fixed at the very top of the viewport, never shifts layout.
 * - Appears only if a load takes > 120 ms (prevents flicker on fast fetches).
 * - Simulates smooth progress toward 85%, then snaps to 100% on complete.
 * - Fades out gracefully after completion.
 *
 * Mount once in App.js (outside <Routes> but inside <Router>).
 */
const GlobalProgressBar = () => {
  const [phase, setPhaseState] = useState('idle'); // 'idle' | 'running' | 'done'
  const [progress, setProgress] = useState(0);

  // Keep phase in a ref so async callbacks always see the latest value
  const phaseRef = useRef('idle');
  const setPhase = useCallback((p) => {
    phaseRef.current = p;
    setPhaseState(p);
  }, []);

  const delayTimerRef = useRef(null);
  const hideTimerRef = useRef(null);
  const tickTimerRef = useRef(null);
  const progressRef = useRef(0);

  const clearTimers = useCallback(() => {
    clearTimeout(delayTimerRef.current);
    clearTimeout(hideTimerRef.current);
    clearTimeout(tickTimerRef.current);
  }, []);

  const startBar = useCallback(() => {
    clearTimers();
    progressRef.current = 8;
    setProgress(8);
    setPhase('running');

    const tick = () => {
      if (phaseRef.current !== 'running') return;
      // Exponential deceleration: quick at first, slows toward 85%
      const remaining = 85 - progressRef.current;
      const delta = Math.max(0.3, remaining * 0.07);
      progressRef.current = Math.min(85, progressRef.current + delta);
      setProgress(progressRef.current);
      tickTimerRef.current = setTimeout(tick, 380);
    };

    tickTimerRef.current = setTimeout(tick, 380);
  }, [clearTimers, setPhase]);

  const completeBar = useCallback(() => {
    clearTimeout(tickTimerRef.current);
    setProgress(100);
    setPhase('done');

    hideTimerRef.current = setTimeout(() => {
      setPhase('idle');
      setProgress(0);
      progressRef.current = 0;
    }, 500); // matches CSS transition duration
  }, [setPhase]);

  useEffect(() => {
    const unsub = progressSignal.subscribe((isLoading) => {
      if (isLoading) {
        // Delay showing the bar — prevents flash on sub-120ms loads
        clearTimeout(delayTimerRef.current);
        delayTimerRef.current = setTimeout(startBar, 120);
      } else {
        clearTimeout(delayTimerRef.current);
        if (phaseRef.current === 'running') {
          completeBar();
        }
      }
    });

    return () => {
      unsub();
      clearTimers();
    };
  }, [startBar, completeBar, clearTimers]);

  if (phase === 'idle') return null;

  const isDone = phase === 'done';

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        zIndex: 10000,
        pointerEvents: 'none',
        background: 'rgba(255,255,255,0.08)',
      }}
    >
      <div
        style={{
          position: 'relative',
          overflow: 'hidden',
          height: '100%',
          width: `${progress}%`,
          background: 'linear-gradient(90deg, #5b4cf0 0%, #7c3aed 45%, #a78bfa 72%, #ede9fe 100%)',
          borderRadius: '0 999px 999px 0',
          boxShadow: '0 0 18px rgba(124,58,237,0.7), 0 0 28px rgba(167,139,250,0.35)',
          // Width animates quickly when running, snaps to 100% on done
          transition: isDone
            ? 'width 0.22s ease-in'
            : 'width 0.45s ease-out',
          // Opacity fades after the width animation completes
          opacity: isDone ? 0 : 1,
          ...(isDone && { transition: 'width 0.22s ease-in, opacity 0.25s ease 0.18s' }),
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '22%',
            minWidth: '36px',
            maxWidth: '120px',
            height: '100%',
            background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(237,233,254,0.75) 60%, rgba(255,255,255,0.98) 100%)',
            filter: 'blur(0.4px)',
            opacity: isDone ? 0.8 : 1,
          }}
        />
      </div>
    </div>
  );
};

export default GlobalProgressBar;
